import { fail } from '@/features/menu/utils/product-page-actions';
import { compactLocalized } from '@/utils/localized';
import { useT } from '@/i18n';
import PushSheet from '@/components/PushSheet';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import { confirmDiscardChanges } from '@/utils/unsavedChanges';
import { useEffect, useRef, useState } from 'react';
import { f7, Link as F7Link, List, ListInput, PageContent, Toolbar } from 'framework7-react';

// ─── Localized-text edit sheet (name / description) ──────────────────────────

type LocalizedSubmit = (
  value: Record<string, string> | undefined,
  done: () => void,
  errOut: (e: unknown) => void,
) => void;

// All sheets below follow Rule 10: always-mounted, `open` prop drives open/close,
// form state lives in a *Content child of <PushSheet>, <Toolbar> is a direct
// child of <PushSheet>, and the wrapper's Save link calls into the child via
// `submitRef`. We never call `f7.sheet.close()` from a submit handler - flipping
// the parent's `open` to false drives the close animation cleanly, so we don't
// race F7's teardown (the `removeChild` crash).
export default function LocalizedTextSheet({
  open,
  onClose,
  title,
  fieldLabel,
  initial,
  required,
  textarea,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  fieldLabel: string;
  initial: Record<string, string>;
  required?: boolean;
  textarea?: boolean;
  onSubmit: LocalizedSubmit;
}) {
  const t = useT();
  const submitRef = useRef<() => void>(() => {});
  return (
    <PushSheet
      open={open}
      onClose={onClose}
      closeByBackdropClick={false}
      style={{ height: 'auto', maxHeight: '90vh' }}
    >
      <Toolbar>
        <div className="left">
          {/* Backdrop-dismiss is off, so Cancel is the only non-Save exit - route
              it through the discard guard so unsaved edits aren't lost silently. */}
          <F7Link onClick={() => void confirmDiscardChanges().then((ok) => ok && onClose())}>
            {t.cancel}
          </F7Link>
        </div>
        <div className="title">{title}</div>
        <div className="right">
          <F7Link onClick={() => submitRef.current()}>{t.saveShort}</F7Link>
        </div>
      </Toolbar>
      <LocalizedTextSheetContent
        open={open}
        onClose={onClose}
        fieldLabel={fieldLabel}
        initial={initial}
        required={required}
        textarea={textarea}
        onSubmit={onSubmit}
        submitRef={submitRef}
      />
    </PushSheet>
  );
}

function LocalizedTextSheetContent({
  open,
  onClose,
  fieldLabel,
  initial,
  required,
  textarea,
  onSubmit,
  submitRef,
}: {
  open: boolean;
  onClose: () => void;
  fieldLabel: string;
  initial: Record<string, string>;
  required?: boolean;
  textarea?: boolean;
  onSubmit: LocalizedSubmit;
  submitRef: { current: () => void };
}) {
  const t = useT();
  const [draft, setDraft] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) setDraft({ ...initial });
  }, [open, initial]);

  const dirty =
    open &&
    JSON.stringify(compactLocalized(draft) ?? {}) !==
      JSON.stringify(compactLocalized({ ...initial }) ?? {});
  useUnsavedGuard(dirty);

  submitRef.current = () => {
    if (required && !draft.en?.trim()) {
      f7.dialog.alert(t.menu.englishValueRequired);
      return;
    }
    onSubmit(compactLocalized(draft), onClose, fail);
  };

  return (
    <PageContent>
      <List strongIos insetIos>
        <ListInput
          label={fieldLabel}
          type={textarea ? 'textarea' : 'text'}
          resizable={textarea}
          required={required}
          validate={required}
          value={draft.en ?? ''}
          onInput={(e) =>
            setDraft({ ...draft, en: (e.target as HTMLInputElement | HTMLTextAreaElement).value })
          }
          errorMessage={t.menu.fieldRequired}
          errorMessageForce={!!required && !draft.en?.trim()}
        />
      </List>
    </PageContent>
  );
}
